import { useState, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { SUBSCRIPTION_PLANS } from '../stripe/StripeConfig'

// Initialize Stripe
// Vite uses import.meta.env instead of process.env
const getStripeKey = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.VITE_STRIPE_PUBLIC_KEY || ''
  }
  return ''
}

const stripePromise = loadStripe(getStripeKey())

interface Subscription {
  id: string
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing'
  plan: 'monthly' | 'yearly'
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  trialEnd?: string
}

interface PaymentMethod {
  id: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

export const useStripe = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create checkout session
  const createCheckoutSession = useCallback(async (planId: 'monthly' | 'yearly', userId: string) => {
    setLoading(true)
    setError(null)

    try {
      // In production, this would call your backend API
      // const response = await fetch('/api/create-checkout-session', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ planId, userId })
      // })
      // const { sessionId } = await response.json()

      // Mock for development
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Redirect to Stripe Checkout
      // const stripe = await stripePromise
      // const { error } = await stripe!.redirectToCheckout({ sessionId })
      // if (error) throw error

      // Mock success
      return { success: true, url: '/payment-success?mock=true' }
    } catch (err: any) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Get subscription details
  const getSubscription = useCallback(async (userId: string): Promise<Subscription | null> => {
    // In production, fetch from your backend
    // const response = await fetch(`/api/subscription/${userId}`)
    // return response.json()

    // Mock subscription
    return {
      id: 'sub_mock_123',
      status: 'trialing',
      plan: 'monthly',
      currentPeriodEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      cancelAtPeriodEnd: false,
      trialEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
    }
  }, [])

  // Cancel subscription
  const cancelSubscription = useCallback(async (subscriptionId: string) => {
    setLoading(true)
    try {
      // In production: POST to /api/cancel-subscription
      await new Promise(resolve => setTimeout(resolve, 1000))
      return { success: true }
    } catch (err: any) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Update subscription (upgrade/downgrade)
  const updateSubscription = useCallback(async (subscriptionId: string, newPlan: 'monthly' | 'yearly') => {
    setLoading(true)
    try {
      // In production: POST to /api/update-subscription
      await new Promise(resolve => setTimeout(resolve, 1000))
      return { success: true }
    } catch (err: any) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Get payment methods
  const getPaymentMethods = useCallback(async (userId: string): Promise<PaymentMethod[]> => {
    // In production: fetch from Stripe
    return [
      {
        id: 'pm_mock_123',
        brand: 'visa',
        last4: '4242',
        expMonth: 12,
        expYear: 2025
      }
    ]
  }, [])

  // Add payment method
  const addPaymentMethod = useCallback(async (userId: string, paymentMethodId: string) => {
    setLoading(true)
    try {
      // In production: POST to /api/add-payment-method
      await new Promise(resolve => setTimeout(resolve, 1000))
      return { success: true }
    } catch (err: any) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Get billing history
  const getBillingHistory = useCallback(async (userId: string) => {
    // In production: fetch from Stripe
    return [
      {
        id: 'in_mock_1',
        date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        amount: 2900,
        status: 'paid',
        description: 'Garden Planner Solo Monthly'
      }
    ]
  }, [])

  return {
    loading,
    error,
    plans: SUBSCRIPTION_PLANS,
    createCheckoutSession,
    getSubscription,
    cancelSubscription,
    updateSubscription,
    getPaymentMethods,
    addPaymentMethod,
    getBillingHistory
  }
}

export default useStripe
